import moment from 'moment'
import { FOURTEEN_DAYS_BEFORE_CHECK_IN, SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import { getFlexibleCancellationDays } from 'lib/offer/cancellationPolicyUtils'

function hasPropertyTimezoneOffset(propertyTimezoneOffset: number | undefined) {
  return propertyTimezoneOffset !== undefined
}

interface FlexibleCancellationTimelineDetailsParams {
  cancellationPolicy: App.OfferCancellationPolicy
  checkInDate?: string | moment.Moment
  propertyTimezoneOffset?: number
  purchaseDate?: string
  today?: moment.Moment
}

export interface FlexibleCancellationTimelineDetails {
  today: moment.Moment
  checkInDate?: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment | undefined
  cancellationAfterChangeOfMindPeriod: boolean
  days: number
}

function getTimelineMoment(
  date: string | moment.Moment | undefined,
  propertyTimezoneOffset: number | undefined,
) {
  if (!date) {
    return undefined
  }

  const parsedDate = moment(date)
  return hasPropertyTimezoneOffset(propertyTimezoneOffset) ? parsedDate.utcOffset(propertyTimezoneOffset, true) : parsedDate
}

export function getFlexibleCancellationTimelineDetails(
  params: FlexibleCancellationTimelineDetailsParams,
): FlexibleCancellationTimelineDetails {
  const {
    cancellationPolicy,
    checkInDate,
    propertyTimezoneOffset,
    purchaseDate,
    today,
  } = params

  const purchaseMoment = purchaseDate ? moment(purchaseDate) : moment()
  const timelineToday = today ?? (hasPropertyTimezoneOffset(propertyTimezoneOffset) ? moment().utcOffset(propertyTimezoneOffset) : moment())
  const days = getFlexibleCancellationDays(cancellationPolicy.type)
  const checkInMoment = getTimelineMoment(checkInDate, propertyTimezoneOffset)

  let startBasedOnToday = hasPropertyTimezoneOffset(propertyTimezoneOffset) ?
      purchaseMoment.clone().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') :
      purchaseMoment.clone().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInMoment?.clone().subtract(days, 'days')

  const changeOfMindDeadline = checkInMoment?.clone().subtract(FOURTEEN_DAYS_BEFORE_CHECK_IN, 'days')
  if (changeOfMindDeadline) {
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline
  }

  const cancellationAfterChangeOfMindPeriod = !!(
    checkInMoment &&
    changeOfMindDeadline &&
    startBasedOnToday.isBefore(changeOfMindDeadline) &&
    purchaseMoment.clone().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(checkInMoment.clone().subtract(days, 'days'))
  )
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  return {
    today: timelineToday,
    checkInDate,
    startBasedOnToday,
    startBasedOnCheckIn,
    cancellationAfterChangeOfMindPeriod,
    days,
  }
}
